/**
 * XSF-501 / XSF-502 / REQ-RS-006 currentness predicate.
 * Read-only: not a second writable store.
 */

export type CarePlanCurrentness = {
  current: boolean;
  /** False when currentness cannot be evaluated (offline / missing evidence). */
  available: boolean;
  label: string;
  planVersion: string;
  writable: false;
};

const ISSUED_STATUSES = new Set(['ACTIVE', 'COMPLETED']);

export function normalizeCarePlanVersionLabel(value: string | number | null | undefined): string {
  if (value == null || value === '') return '';
  const raw = String(value).trim();
  if (!raw) return '';
  return /^v/i.test(raw) ? `v${raw.slice(1)}` : `v${raw}`;
}

export function evaluateCarePlanCurrentness(input: {
  planVersion?: string | number | null;
  activeVersion?: string | number | null;
  status?: string | null;
}): CarePlanCurrentness {
  const planVersion = normalizeCarePlanVersionLabel(input.planVersion) || 'unversioned';
  const activeVersion = normalizeCarePlanVersionLabel(input.activeVersion);
  const status = String(input.status ?? '').toUpperCase();
  const issued = ISSUED_STATUSES.has(status);
  const current = issued && activeVersion.length > 0 && planVersion === activeVersion;
  return {
    current,
    available: true,
    planVersion,
    writable: false,
    label: current ? `Current as of ${planVersion}` : 'Not current',
  };
}

/** Offline / missing-evidence label. Not a boolean current/not-current claim. */
export function unavailableCarePlanCurrentness(
  planVersion?: string | number | null,
): CarePlanCurrentness {
  return {
    current: false,
    available: false,
    planVersion: normalizeCarePlanVersionLabel(planVersion) || 'unversioned',
    writable: false,
    label: 'Currentness unavailable',
  };
}
